import { ReactElement, createContext, useContext, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useDebounceValue } from 'usehooks-ts'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { ManifestsType } from 'modules/core/lib/manifests/manager'

import { ActivityContainer, RenderedActivity } from './activity'
import { ActivityCatalog, ExtractActivity, getActivity } from './catalog'
import { ActivityComponent, ActivityDefinitionProps } from './types'

type OpenActivityCallback = <T extends ActivityCatalog>(
  id: T,
  data?: ActivityDefinitionProps<ExtractActivity<ManifestsType, T>>
) => void

const ActivitiesContext = createContext<{
  currentActivity: RenderedActivity | null
  openActivity: OpenActivityCallback
  closeActivity: () => void
  toggleActivity: OpenActivityCallback
} | null>(null)

export const ActivitiesProvider: React.FC<{
  children: ReactElement
  renderActivities?: boolean
}> = ({ children, renderActivities = true }) => {
  const { search } = useLocation()
  const { currentActivity } = useCurrentActivity()
  const hasCurrentActivity = !!currentActivity

  const [hasCurrentActivityDebounced] = useDebounceValue(hasCurrentActivity, 100)

  const hasCurrentActivityRef = useRef(hasCurrentActivityDebounced)

  useEffect(() => {
    hasCurrentActivityRef.current = hasCurrentActivityDebounced
  }, [hasCurrentActivityDebounced])

  const navigate = useNavigate()

  const openActivity: OpenActivityCallback = useUnsafeCallback(
    (id, data) => {
      navigate({
        search,
        hash: encodeActivity(id, data),
      })
    },
    [navigate, search]
  )

  const closeActivity = useUnsafeCallback(() => {
    navigate({
      search,
      hash: '',
    })
  }, [navigate, search])

  const toggleActivity: OpenActivityCallback = useUnsafeCallback(
    (id, data) => {
      if (hasCurrentActivityRef.current) {
        closeActivity()
      } else {
        openActivity(id, data)
      }
    },
    [openActivity, closeActivity]
  )

  const contextValue = useUnsafeMemo(
    () => ({ currentActivity, openActivity, closeActivity, toggleActivity }),
    [currentActivity, openActivity, closeActivity, toggleActivity]
  )

  return (
    <ActivitiesContext.Provider value={contextValue}>
      {children}
      {renderActivities && (
        <ActivityContainer activity={currentActivity} closeActivity={closeActivity} />
      )}
    </ActivitiesContext.Provider>
  )
}

export const encodeActivity = (id: string, data?: Record<string, string>) => {
  if (!data) return id

  return `${id}/${btoa(JSON.stringify(data))}`
}

export const decodeActivity = (hash: string) => {
  const [id, params] = hash.replace(/^#/, '').split('/')
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const activity = id ? getActivity(id as ActivityCatalog) : null

  return {
    activity,
    data: params ? JSON.parse(atob(params)) : {},
  }
}

const useCurrentActivity = (): { currentActivity: RenderedActivity | null } => {
  const { hash } = useLocation()

  // During testing, there's a typing bug and `hash` can be `undefined`
  const { activity, data } = decodeActivity(hash || '')

  return {
    currentActivity: activity
      ? {
          data,
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          component: activity.component as ActivityComponent<Record<string, string>>,
        }
      : null,
  }
}

export const useActivities = () => {
  const activityContext = useContext(ActivitiesContext)
  if (!activityContext) throw new Error('useActivities must be used within an ActivityProvider')

  return activityContext
}

export const ActivitiesRenderer: React.FC = () => {
  const { currentActivity, closeActivity } = useActivities()

  return <ActivityContainer activity={currentActivity} closeActivity={closeActivity} />
}
